"use client";

import * as React from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getClipboardDataFromSelection,
  setLexicalClipboardDataTransfer,
  type LexicalClipboardData,
} from "@lexical/clipboard";
import { mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isNodeSelection,
  COMMAND_PRIORITY_HIGH,
  COPY_COMMAND,
  CUT_COMMAND,
} from "lexical";

import { writeRichTextClipboardFromData } from "../helpers/richTextBlockClipboard";

function isClipboardEvent(e: unknown): e is ClipboardEvent {
  return typeof ClipboardEvent !== "undefined" && e instanceof ClipboardEvent;
}

/**
 * Lexical 默认 `copyToClipboard` 会校验 DOM selection 是否在编辑器内；Decorator/图片块的 NodeSelection
 * 常不满足，导致不写剪贴板。⌘C/⌘X 传入的是 KeyboardEvent，也没有 `clipboardData`，必须在**当前同步更新内**
 * 序列化并 `navigator.clipboard.write`（否则会走 `execCommand('copy')` 临时 DOM 选区，破坏 NodeSelection，
 * 剪切时删不到图片块）。
 *
 * 注意：`COPY_COMMAND` / `CUT_COMMAND` 已在 `dispatchCommand` → `updateEditorSync` 的可写上下文中执行，
 * 此处**禁止**再包一层 `editor.update()`。
 */
export function NodeSelectionClipboardPlugin() {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        COPY_COMMAND,
        (event) => {
          const selection = $getSelection();
          if (!$isNodeSelection(selection) || selection.getNodes().length === 0) return false;
          const data = $getClipboardDataFromSelection(selection) as unknown as LexicalClipboardData;
          const dataRecord = data as unknown as Record<string, string>;
          const lexical = dataRecord["application/x-lexical-editor"];
          if (lexical == null || lexical === "") return false;

          if (isClipboardEvent(event) && event.clipboardData != null) {
            setLexicalClipboardDataTransfer(event.clipboardData, data);
            event.preventDefault();
            return true;
          }

          void writeRichTextClipboardFromData(dataRecord);
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        CUT_COMMAND,
        (event) => {
          const selection = $getSelection();
          if (!$isNodeSelection(selection) || selection.getNodes().length === 0) return false;
          const data = $getClipboardDataFromSelection(selection) as unknown as LexicalClipboardData;
          const dataRecord = data as unknown as Record<string, string>;
          const lexical = dataRecord["application/x-lexical-editor"];
          if (lexical == null || lexical === "") return false;

          if (isClipboardEvent(event) && event.clipboardData != null) {
            setLexicalClipboardDataTransfer(event.clipboardData, data);
            event.preventDefault();
          } else {
            void writeRichTextClipboardFromData(dataRecord);
          }

          for (const n of selection.getNodes()) n.remove();
          const root = $getRoot();
          if (root.getChildrenSize() === 0) root.append($createParagraphNode());
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor]);

  return null;
}
